// CR176/CR317: Canvas-based image downscaling for oversized flyer pages
// Generates OCR-safe images with configurable downscale strategy and safety floor

import { 
  OCR_MAX_MEGAPIXELS, 
  OCR_SAFE_TARGET_MEGAPIXELS, 
  OCR_MIN_WIDTH 
} from '@/components/admin/batch-runs/flyer-truth-table/types';
import type { DownscaleConfig } from '@/components/admin/batch-runs/s0-settings/types';
import { DEFAULT_LARGE_PAGES_CONFIG } from '@/components/admin/batch-runs/s0-settings/types';

export interface DownscaleResult {
  /** The downscaled image as a data URL or blob URL */
  imageUrl: string;
  /** Original dimensions */
  originalWidth: number;
  originalHeight: number;
  originalMegapixels: number;
  /** Whether downscaling was performed */
  wasDownscaled: boolean;
  /** New dimensions (if downscaled) */
  newWidth: number;
  newHeight: number;
  newMegapixels: number;
  /** Error if downscaling failed */
  error?: string;
}

/** CR317: Extended result with diagnostics */
export interface DownscaleResultWithDiagnostics extends DownscaleResult {
  diagnostics: DownscaleDiagnostics;
}

/** CR317: Diagnostics for debugging large page handling */
export interface DownscaleDiagnostics {
  targetMax: number;
  effectiveTargetMax: number;
  floorApplied: boolean;
  thresholdChecked: boolean;
  thresholdPassed: boolean;
  scale: number;
  neverUpscaleApplied: boolean;
}

/**
 * Check if an image needs downscaling based on megapixel count (legacy)
 */
export function needsDownscaling(width: number, height: number): boolean {
  const megapixels = (width * height) / 1_000_000;
  return megapixels > OCR_MAX_MEGAPIXELS;
}

/**
 * CR317: Check if downscaling should be applied based on config thresholds
 */
export function shouldApplyDownscale(
  width: number, 
  height: number, 
  config: DownscaleConfig
): { shouldApply: boolean; reason: string } {
  if (!config.enabled) {
    return { shouldApply: false, reason: 'downscale_disabled' };
  }

  const maxDim = Math.max(width, height);
  
  if (config.applyWhen.enabled) {
    if (maxDim < config.applyWhen.minDimensionPx) {
      return { 
        shouldApply: false, 
        reason: `below_threshold_${maxDim}<${config.applyWhen.minDimensionPx}` 
      };
    }
  }

  return { shouldApply: true, reason: 'threshold_passed' };
}

/**
 * CR317: Calculate target dimensions with safety floor enforcement
 */
export function calculateDownscaleDimensions(
  width: number,
  height: number,
  config: DownscaleConfig
): { 
  width: number; 
  height: number; 
  diagnostics: DownscaleDiagnostics;
} {
  const maxDim = Math.max(width, height);
  const targetMax = config.maxDimensionPx;
  
  // Apply safety floor
  const effectiveTargetMax = Math.max(targetMax, config.minOutputMaxDimensionPx);
  const floorApplied = targetMax < config.minOutputMaxDimensionPx;
  
  // Compute scale
  let scale = effectiveTargetMax / maxDim;
  
  // Never upscale rule
  const neverUpscaleApplied = scale >= 1.0;
  if (neverUpscaleApplied) {
    scale = 1.0;
  }
  
  const newWidth = Math.floor(width * scale);
  const newHeight = Math.floor(height * scale);
  
  const diagnostics: DownscaleDiagnostics = {
    targetMax,
    effectiveTargetMax,
    floorApplied,
    thresholdChecked: config.applyWhen.enabled,
    thresholdPassed: true, // If we got here, threshold passed
    scale,
    neverUpscaleApplied,
  };
  
  return { width: newWidth, height: newHeight, diagnostics };
}

/**
 * Calculate target dimensions for OCR-safe downscaling (legacy)
 * Preserves aspect ratio while targeting OCR_SAFE_TARGET_MEGAPIXELS
 * Enforces OCR_MIN_WIDTH minimum for text readability
 */
export function calculateOcrSafeDimensions(
  width: number, 
  height: number
): { width: number; height: number; megapixels: number } {
  const currentMp = (width * height) / 1_000_000;
  
  if (currentMp <= OCR_MAX_MEGAPIXELS) {
    // No downscaling needed
    return { width, height, megapixels: currentMp };
  }
  
  // Calculate scale to reach target megapixels
  let scale = Math.sqrt((OCR_SAFE_TARGET_MEGAPIXELS * 1_000_000) / (width * height));
  scale = Math.min(scale, 1.0); // Never upscale
  
  let newWidth = Math.floor(width * scale);
  let newHeight = Math.floor(height * scale);
  
  // Apply minimum width guard for readability
  if (newWidth < OCR_MIN_WIDTH) {
    const minScale = OCR_MIN_WIDTH / width;
    newWidth = OCR_MIN_WIDTH;
    newHeight = Math.floor(height * minScale);
  }
  
  // Final check: if min-width caused MP to exceed max, cap to max
  let newMp = (newWidth * newHeight) / 1_000_000;
  if (newMp > OCR_MAX_MEGAPIXELS) {
    const maxScale = Math.sqrt((OCR_MAX_MEGAPIXELS * 1_000_000) / (width * height));
    newWidth = Math.floor(width * maxScale);
    newHeight = Math.floor(height * maxScale);
    newMp = (newWidth * newHeight) / 1_000_000;
  }
  
  return {
    width: newWidth,
    height: newHeight,
    megapixels: newMp,
  };
}

/**
 * Load an image from URL and get its dimensions
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Required for CORS images
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Failed to load image: ${e}`));
    img.src = url;
  });
}

/**
 * Downscale an image using Canvas API
 * Returns the downscaled image as a data URL (base64 PNG)
 */
export async function downscaleImage(
  imageUrl: string,
  targetWidth: number,
  targetHeight: number
): Promise<string> {
  const img = await loadImage(imageUrl);
  
  // Create canvas with target dimensions
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }
  
  // Use high-quality image smoothing for better OCR results
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw the image scaled to target dimensions
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  
  // Return as PNG data URL (lossless for better OCR)
  return canvas.toDataURL('image/png', 1.0);
}

/**
 * Prepare an image for OCR, downscaling if necessary (legacy entry point)
 * This is the main entry point for CR176 OCR-safe image generation
 */
export async function prepareImageForOcr(imageUrl: string): Promise<DownscaleResult> {
  try {
    // Load image to get dimensions
    const img = await loadImage(imageUrl);
    const originalWidth = img.naturalWidth;
    const originalHeight = img.naturalHeight;
    const originalMp = (originalWidth * originalHeight) / 1_000_000;
    
    console.log(`[ImageDownscaler] Image loaded: ${originalWidth}×${originalHeight} (${originalMp.toFixed(1)}MP)`);
    
    // Check if downscaling is needed
    if (!needsDownscaling(originalWidth, originalHeight)) {
      console.log(`[ImageDownscaler] No downscaling needed (<${OCR_MAX_MEGAPIXELS}MP)`);
      return {
        imageUrl,
        originalWidth,
        originalHeight,
        originalMegapixels: originalMp,
        wasDownscaled: false,
        newWidth: originalWidth,
        newHeight: originalHeight,
        newMegapixels: originalMp,
      };
    }
    
    // Calculate target dimensions
    const target = calculateOcrSafeDimensions(originalWidth, originalHeight);
    console.log(`[ImageDownscaler] Downscaling: ${originalWidth}×${originalHeight} → ${target.width}×${target.height} (${target.megapixels.toFixed(1)}MP)`);
    
    // Perform downscale
    const downscaledUrl = await downscaleImage(imageUrl, target.width, target.height);
    
    return {
      imageUrl: downscaledUrl,
      originalWidth,
      originalHeight,
      originalMegapixels: originalMp,
      wasDownscaled: true,
      newWidth: target.width,
      newHeight: target.height,
      newMegapixels: target.megapixels,
    };
  } catch (err) {
    console.error('[ImageDownscaler] Failed to prepare image:', err);
    
    // Return error result - caller should handle appropriately
    return {
      imageUrl,
      originalWidth: 0,
      originalHeight: 0,
      originalMegapixels: 0,
      wasDownscaled: false,
      newWidth: 0,
      newHeight: 0,
      newMegapixels: 0,
      error: err instanceof Error ? err.message : 'Unknown error preparing image',
    };
  }
}

/**
 * CR317: Prepare an image for OCR with configurable downscale strategy and safety floor
 * Returns extended result with diagnostics for debugging
 */
export async function prepareImageForOcrWithConfig(
  imageUrl: string,
  config?: DownscaleConfig
): Promise<DownscaleResultWithDiagnostics> {
  // Use default config if not provided
  const effectiveConfig = config || DEFAULT_LARGE_PAGES_CONFIG.downscale;
  
  try {
    // Load image to get dimensions
    const img = await loadImage(imageUrl);
    const originalWidth = img.naturalWidth;
    const originalHeight = img.naturalHeight;
    const originalMp = (originalWidth * originalHeight) / 1_000_000;
    
    console.log(`[ImageDownscaler] Image loaded: ${originalWidth}×${originalHeight} (${originalMp.toFixed(1)}MP)`);
    
    // Check if downscaling should be applied based on config
    const { shouldApply, reason } = shouldApplyDownscale(originalWidth, originalHeight, effectiveConfig);
    
    if (!shouldApply) {
      console.log(`[ImageDownscaler] Downscaling skipped: ${reason}`);
      return {
        imageUrl,
        originalWidth,
        originalHeight,
        originalMegapixels: originalMp,
        wasDownscaled: false,
        newWidth: originalWidth,
        newHeight: originalHeight,
        newMegapixels: originalMp,
        diagnostics: {
          targetMax: effectiveConfig.maxDimensionPx,
          effectiveTargetMax: Math.max(effectiveConfig.maxDimensionPx, effectiveConfig.minOutputMaxDimensionPx),
          floorApplied: false,
          thresholdChecked: effectiveConfig.applyWhen.enabled,
          thresholdPassed: false,
          scale: 1.0,
          neverUpscaleApplied: false,
        },
      };
    }
    
    // Calculate target dimensions with floor enforcement
    const { width: targetWidth, height: targetHeight, diagnostics } = calculateDownscaleDimensions(
      originalWidth, 
      originalHeight, 
      effectiveConfig
    );
    
    // If scale is 1.0, no actual downscaling needed
    if (diagnostics.neverUpscaleApplied) {
      console.log(`[ImageDownscaler] No downscaling needed (would upscale)`);
      return {
        imageUrl,
        originalWidth,
        originalHeight,
        originalMegapixels: originalMp,
        wasDownscaled: false,
        newWidth: originalWidth,
        newHeight: originalHeight,
        newMegapixels: originalMp,
        diagnostics,
      };
    }
    
    console.log(`[ImageDownscaler] Downscaling: ${originalWidth}×${originalHeight} → ${targetWidth}×${targetHeight} (scale=${diagnostics.scale.toFixed(2)}, floor=${diagnostics.floorApplied})`);
    
    // Perform downscale
    const downscaledUrl = await downscaleImage(imageUrl, targetWidth, targetHeight);
    const newMp = (targetWidth * targetHeight) / 1_000_000;
    
    return {
      imageUrl: downscaledUrl,
      originalWidth,
      originalHeight,
      originalMegapixels: originalMp,
      wasDownscaled: true,
      newWidth: targetWidth,
      newHeight: targetHeight,
      newMegapixels: newMp,
      diagnostics,
    };
  } catch (err) {
    console.error('[ImageDownscaler] Failed to prepare image:', err);
    
    // Return error result with empty diagnostics
    return {
      imageUrl,
      originalWidth: 0,
      originalHeight: 0,
      originalMegapixels: 0,
      wasDownscaled: false,
      newWidth: 0,
      newHeight: 0,
      newMegapixels: 0,
      error: err instanceof Error ? err.message : 'Unknown error preparing image',
      diagnostics: {
        targetMax: effectiveConfig.maxDimensionPx,
        effectiveTargetMax: Math.max(effectiveConfig.maxDimensionPx, effectiveConfig.minOutputMaxDimensionPx),
        floorApplied: false,
        thresholdChecked: false,
        thresholdPassed: false,
        scale: 1.0,
        neverUpscaleApplied: false,
      },
    };
  }
}

/**
 * CR317: Generate debug sample entry for step diagnostics
 */
export function createLargePagesDebugSample(
  pageNumber: number,
  originalWidth: number,
  originalHeight: number,
  scaledWidth: number,
  scaledHeight: number,
  diagnostics: DownscaleDiagnostics
): Record<string, unknown> {
  return {
    page: pageNumber,
    original: `${originalWidth}x${originalHeight}`,
    scaled: `${scaledWidth}x${scaledHeight}`,
    scale: parseFloat(diagnostics.scale.toFixed(2)),
    targetMax: diagnostics.targetMax,
    effectiveTargetMax: diagnostics.effectiveTargetMax,
    floorApplied: diagnostics.floorApplied,
  };
}
